/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Grid } from '@coursera/cds-core';

import EnterpriseMobileSearchBarLegacyGridItem from 'bundles/composable-page-header-enterprise/components/EnterpriseMobileSearchBarLegacyGridItem';
import type { UseEnterpriseMyPageHeaderResult as MyEnterprisePageHeader } from 'bundles/composable-page-header-enterprise/contexts/EnterpriseMyPageHeaderContext';
import DefaultLoggedInEnterpriseMobileMenuConfig from 'bundles/composable-page-header-enterprise/module-renderers/DefaultLoggedInEnterpriseMobileMenuConfig';
import {
  getEnterpriseLogoLabel,
  getLinkForEnterpriseProgram,
} from 'bundles/composable-page-header-enterprise/utils/enterpriseLogoUtils';
import MainNavigationWrapper from 'bundles/composable-page-header/components/MainNavigationWrapper';
import NotificationsLegacyGridItem from 'bundles/composable-page-header/components/grid-items/NotificationsLegacyGridItem';
import CourseraHomeLogo from 'bundles/composable-page-header/components/grid-items/home-logo/CourseraHomeLogo';
import HomeLogoGridItemModule from 'bundles/composable-page-header/components/grid-items/home-logo/HomeLogoGridItemModule';
import MobileMenuIconGridItem from 'bundles/composable-page-header/components/grid-items/mobile/MobileMenuIconGridItem';
import { LOGO_WIDTH } from 'bundles/composable-page-header/components/program-switcher/ProgramCard';
import EnterpriseLogo from 'bundles/page-header/components/EnterpriseLogo';
import type { PageNavLink } from 'bundles/unified-common/hooks/useEnterprisePageHeaderLinks';

const styles = {
  fullContainer: css`
    flex-wrap: nowrap;
  `,
  leftSide: css`
    flex-wrap: nowrap;
  `,
  rightSide: css`
    flex-wrap: nowrap;
  `,
};

type Props = {
  subnavLinks?: PageNavLink[];
  pageHeaderData: MyEnterprisePageHeader;
};

export default function LoggedInEnterpriseMobileMainNav({ subnavLinks, pageHeaderData }: Props) {
  const {
    enrolledDegreePrograms,
    enrolledEnterprisePrograms,
    chosenDegree,
    chosenEnterpriseProgram,
    hasEnrolledPrograms,
    naptimeManagedThirdPartyOrganizations,
    hasCheatingIncidents,
    canViewEducatorAdminLink,
    currentPageProgram,
  } = pageHeaderData;

  const LogoComponent = (
    <HomeLogoGridItemModule
      href={currentPageProgram ? getLinkForEnterpriseProgram(currentPageProgram) : '/'}
      logos={
        currentPageProgram
          ? [
              <CourseraHomeLogo key="1" />,
              <EnterpriseLogo
                key="2"
                preferRectangle={false}
                thirdPartyOrganization={currentPageProgram?.naptimeThirdPartyOrganization}
                program={{
                  name: currentPageProgram?.name,
                  metadata: { squareLogo: currentPageProgram?.squareLogo },
                }}
                logoWidth={LOGO_WIDTH}
              />,
            ]
          : [<CourseraHomeLogo key="1" />]
      }
      ariaLabel={currentPageProgram ? getEnterpriseLogoLabel(currentPageProgram) : undefined}
    />
  );

  return (
    <MainNavigationWrapper>
      <Grid item container css={styles.fullContainer}>
        <Grid item container alignItems="center" css={styles.leftSide}>
          <MobileMenuIconGridItem
            menuConfig={
              <DefaultLoggedInEnterpriseMobileMenuConfig
                enrolledDegreePrograms={enrolledDegreePrograms}
                enrolledEnterprisePrograms={enrolledEnterprisePrograms}
                chosenDegree={chosenDegree}
                chosenEnterpriseProgram={chosenEnterpriseProgram}
                hasEnrolledPrograms={!!hasEnrolledPrograms}
                naptimeManagedThirdPartyOrganizations={naptimeManagedThirdPartyOrganizations}
                hasCheatingIncidents={hasCheatingIncidents}
                canViewEducatorAdminLink={canViewEducatorAdminLink}
                subNavLinks={subnavLinks}
              />
            }
            homeLogo={LogoComponent}
          />
          {LogoComponent}
        </Grid>
        <Grid item container alignItems="center" justifyContent="flex-end" css={styles.rightSide}>
          <NotificationsLegacyGridItem />
          <EnterpriseMobileSearchBarLegacyGridItem currentProgram={currentPageProgram} />
        </Grid>
      </Grid>
    </MainNavigationWrapper>
  );
}
